import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { LogOutIcon, MonitorIcon, MoonIcon, SunIcon } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@app/components/ui/button'
import { logout, sessionQuery } from '@app/features/auth/api'
import { SyncFreshness } from '@app/features/sync/freshness-chip'
import { api, queryKeys } from '@app/lib/api'
import { cn } from '@app/lib/cn'
import { activeNavItem, navForRole, type NavItem } from '@app/lib/nav'
import { queryClient } from '@app/lib/query-client'
import { LEAST_PRIVILEGED_ROLE } from '../../worker/api/auth.contract'

export function AppShell() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const current = activeNavItem(pathname)
  /*
   * The shell only renders behind the route guard, which awaits the session
   * query before this component mounts — so `user` is there. The fallback is
   * the least privileged role all the same: if that ever stops being true, the
   * sidebar should come up short rather than offer links that 403.
   */
  const { data: user } = useQuery(sessionQuery)

  return (
    <div className="flex min-h-dvh bg-kagit">
      <Sidebar pathname={pathname} nav={navForRole(user?.role ?? LEAST_PRIVILEGED_ROLE)} />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-12 shrink-0 items-center justify-between gap-4 border-b border-hairline bg-surface/85 px-5 backdrop-blur-sm">
          <h1 className="font-display text-lg font-semibold tracking-tight text-ink">
            {current?.label ?? 'Panel'}
          </h1>
          <div className="flex items-center gap-2">
            {/* How far the mirror reaches — every screen reads it, so it is
                stated once here, for every role. Brings its own hairline. */}
            <SyncFreshness />
            <ThemeToggle />
            {/* House style is hairlines, not gaps, wherever two things differ in kind. */}
            <span aria-hidden className="h-4 w-px shrink-0 bg-hairline" />
            <UserChip />
          </div>
        </header>

        <main className="min-w-0 flex-1 px-3 py-4 md:px-5 md:py-5">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

/* ── Theme ──────────────────────────────────────────────────────────────────
 *
 * Three states behind one button: the paper theme, the workshop-at-night theme,
 * and "follow the machine". `system` is a real choice rather than the absence of
 * one — the office machines are set to switch at dusk, and a user who picks it
 * expects the panel to switch with everything else on the screen.
 *
 * The tokens live in styles.css; all this layer does is decide which of the two
 * words ends up in `data-theme` on <html>, and remember the answer.
 * ─────────────────────────────────────────────────────────────────────────── */

type ThemePreference = 'light' | 'dark' | 'system'

/** Shared with the pre-paint script in index.html — the two must agree. */
const THEME_STORAGE_KEY = 'panel-theme'

const THEME_LABEL: Record<ThemePreference, string> = {
  light: 'Açık tema',
  dark: 'Koyu tema',
  system: 'Sistem',
}

const THEME_ICON = {
  light: SunIcon,
  dark: MoonIcon,
  system: MonitorIcon,
} as const

/** The cycle the button walks. */
const NEXT_THEME: Record<ThemePreference, ThemePreference> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
}

const DARK_QUERY = '(prefers-color-scheme: dark)'

function readThemePreference(): ThemePreference {
  let stored: string | null = null
  try {
    stored = localStorage.getItem(THEME_STORAGE_KEY)
  } catch {
    // Storage can throw outright in private mode; the OS preference still works.
  }
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system'
}

/**
 * Resolves `system` and writes the answer to <html>. The stylesheet is never
 * shown a third state — it only ever matches `[data-theme='dark']`.
 */
function applyThemePreference(preference: ThemePreference): void {
  const resolved =
    preference === 'system' ? (window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light') : preference
  document.documentElement.dataset.theme = resolved

  /*
   * The mobile address-bar colour, read back off the token that just changed
   * rather than restated here — styles.css stays the only place either palette
   * is written down.
   */
  const ground = getComputedStyle(document.documentElement).getPropertyValue('--color-bg').trim()
  if (ground) document.querySelector('meta[name="theme-color"]')?.setAttribute('content', ground)
}

/**
 * Called once from main.tsx, before React renders.
 *
 * index.html already applied the stored preference synchronously — that is what
 * keeps the first paint from flashing paper — so this repeats the same answer
 * and repaints nothing. What it adds is the part a static script cannot do:
 * while the preference is `system`, the panel follows the machine as it flips
 * at dusk, without a reload.
 */
/*
 * The preference of record. localStorage is only its persistence, not its
 * source of truth: when storage is unavailable (private mode, quota), the
 * toggle's choice must still hold for the tab — re-reading storage in the OS
 * listener would silently fall back to 'system' and override it (codex P2).
 */
let currentPreference: ThemePreference = 'system'

export function initTheme(): void {
  currentPreference = readThemePreference()
  applyThemePreference(currentPreference)
  window.matchMedia(DARK_QUERY).addEventListener('change', () => {
    if (currentPreference === 'system') applyThemePreference('system')
  })
}

/**
 * Topbar control: sun / moon / monitor.
 *
 * The icon is the setting, the label is the action — one button cannot show
 * three states any other way, and a label that only named the state ("Koyu
 * tema") would leave the reader guessing what pressing it does.
 */
function ThemeToggle() {
  const [preference, setPreference] = useState<ThemePreference>(() => currentPreference)
  const Icon = THEME_ICON[preference]
  const label = `Temayı değiştir — şu an: ${THEME_LABEL[preference]}`

  function cycle() {
    const next = NEXT_THEME[preference]
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      // Unwritable storage: the choice still applies, it just won't outlive the tab.
    }
    currentPreference = next
    applyThemePreference(next)
    setPreference(next)
  }

  return (
    <Button variant="ghost" size="icon" onClick={cycle} title={label} aria-label={label}>
      <Icon aria-hidden />
    </Button>
  )
}

function Sidebar({ pathname, nav }: { pathname: string; nav: readonly NavItem[] }) {
  return (
    /*
     * Below lg (1024px) the sidebar collapses to an icon rail — labels return
     * with the space. The cut-off is the lg breakpoint, not md, because these
     * screens are tables: at 834px (an iPad in portrait, what actually sits on
     * the shop floor) a 224px label column costs the stok/sipariş tables two
     * columns they cannot spare. Every item keeps its `title`, so the label is
     * one hover away, and the kordovan left border keeps the active item
     * readable with the text gone.
     */
    <aside className="sticky top-0 flex h-dvh w-13 shrink-0 flex-col border-r border-hairline bg-surface lg:w-56">
      <div className="flex h-12 items-center gap-2.5 border-b border-hairline px-3.5 lg:px-4">
        <span
          aria-hidden
          className="grid size-6 shrink-0 place-items-center rounded-xs bg-kordovan font-display text-[0.8125rem] font-semibold text-kagit"
        >
          P
        </span>
        <span className="hidden font-display text-base font-semibold tracking-tight text-ink lg:block">
          Panel
        </span>
      </div>

      <nav aria-label="Ana menü" className="flex-1 overflow-y-auto p-1.5 lg:p-2">
        <ul className="flex flex-col gap-0.5">
          {nav.map((item) => {
            const isActive = item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)
            const Icon = item.icon
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  title={item.label}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex items-center justify-center gap-2.5 rounded-xs border-l-2 py-1.5 pl-2 pr-2 text-data transition-colors lg:justify-start lg:pl-2.5',
                    isActive
                      ? 'border-l-kordovan bg-kordovan/7 font-medium text-kordovan'
                      : 'border-l-transparent text-ink-muted hover:bg-kagit hover:text-ink',
                  )}
                >
                  <Icon className="size-4 shrink-0" aria-hidden />
                  <span className="hidden lg:inline">{item.label}</span>
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>

      <DbStatus />
    </aside>
  )
}

/** Live check that the Worker can still reach Postgres through Hyperdrive. */
function DbStatus() {
  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.health,
    queryFn: api.health,
    refetchInterval: 60_000,
  })

  const connected = data?.ok === true && data.db === 1
  const state = isPending ? 'pending' : isError || !connected ? 'down' : 'up'

  const dot = {
    pending: 'bg-ink-muted/40',
    up: 'bg-zeytin',
    down: 'bg-kordovan',
  }[state]

  const text = {
    pending: 'Bağlantı denetleniyor',
    up: 'Veritabanı bağlı',
    down: 'Veritabanına ulaşılamıyor',
  }[state]

  return (
    <div
      title={text}
      className="flex items-center gap-2 border-t border-hairline px-4.5 py-2.5 lg:px-4"
    >
      <span aria-hidden className={cn('size-1.5 shrink-0 rounded-full', dot)} />
      {/* Hidden visually on the icon rail, still announced by screen readers. */}
      <span className="text-micro text-ink-muted max-lg:sr-only">{text}</span>
    </div>
  )
}

/** Signed-in user + logout. The shell only ever renders behind the route guard. */
function UserChip() {
  const navigate = useNavigate()
  const { data: user } = useQuery(sessionQuery)

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSettled: () => {
      // Clear local state no matter what the server said: an expired or revoked
      // session must still end up logged out on this side.
      queryClient.clear()
      void navigate({ to: '/giris', replace: true })
    },
  })

  return (
    <div className="flex items-center gap-2.5">
      <span
        aria-hidden
        className="grid size-7 shrink-0 place-items-center rounded-xs bg-kordovan/10 font-display text-micro font-semibold text-kordovan"
      >
        {initials(user?.name)}
      </span>
      <span className="hidden min-w-0 leading-tight sm:block">
        <span className="block truncate text-data font-medium text-ink">{user?.name ?? '—'}</span>
        <span className="block truncate text-micro text-ink-muted">{user?.email ?? ''}</span>
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => logoutMutation.mutate()}
        disabled={logoutMutation.isPending}
        title="Çıkış yap"
      >
        <LogOutIcon aria-hidden />
        <span className="max-sm:sr-only">Çıkış</span>
      </Button>
    </div>
  )
}

/** "Ali Sütçü" → "AS"; for single-word names the first two letters. */
function initials(name: string | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '—'
  const letters =
    parts.length === 1
      ? (parts[0] ?? '').slice(0, 2)
      : `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`
  return letters.toLocaleUpperCase('tr-TR')
}
